import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../auth/AuthContext";
import useAutoRetry from "../hooks/useAutoRetry";
import {
  cacheClassrooms,
  chooseClassroom,
  getCachedClassrooms,
  getChosenClassroom,
  listMyClassrooms,
} from "./classrooms";
import { DENIED, RETRYABLE, problemOf } from "./problems";

// The classes the signed-in teacher teaches, and which one they are teaching
// now. With one class it is simply that one; with several, the teacher picks
// once and it is remembered.
const ClassroomContext = createContext(null);

const initialState = (userId) => {
  const cached = getCachedClassrooms(userId);
  return cached
    ? { status: "ready", classrooms: cached, fromCache: true, problem: null }
    : { status: "loading", classrooms: [], fromCache: false, problem: null };
};

export const ClassroomProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user.id;
  const [state, setState] = useState(() => initialState(userId));
  const [chosen, setChosen] = useState(() => getChosenClassroom(userId));

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) {
        setState((s) =>
          s.status === "error" ? { ...s, status: "loading", problem: null } : s
        );
      }
      try {
        const classrooms = await listMyClassrooms();
        cacheClassrooms(userId, classrooms);
        setState({ status: "ready", classrooms, fromCache: false, problem: null });
      } catch (error) {
        const problem = problemOf(error);
        // Classes saved on this device keep the app usable offline, but not
        // once access has been refused.
        setState((s) =>
          s.fromCache && problem !== DENIED
            ? { ...s, problem }
            : { status: "error", classrooms: [], fromCache: false, problem }
        );
      }
    },
    [userId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Keep checking while the teacher has no class yet or the last load failed.
  const autoRetrying =
    RETRYABLE.has(state.problem) ||
    (state.status === "ready" && state.classrooms.length === 0);
  const retryQuietly = useCallback(() => load({ quiet: true }), [load]);
  useAutoRetry(autoRetrying, retryQuietly);

  const choose = useCallback(
    (classroomId) => {
      chooseClassroom(userId, classroomId);
      setChosen(classroomId);
    },
    [userId]
  );

  const value = useMemo(() => {
    const { classrooms } = state;
    const active =
      classrooms.find((c) => c.id === chosen) ||
      (classrooms.length === 1 ? classrooms[0] : null);
    return {
      ...state,
      active,
      choose,
      retry: () => load(),
      autoRetrying,
    };
  }, [state, chosen, choose, load, autoRetrying]);

  return (
    <ClassroomContext.Provider value={value}>{children}</ClassroomContext.Provider>
  );
};

export const useClassrooms = () => useContext(ClassroomContext);
